import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, Outlet } from 'react-router-dom';

import { routes } from '@/config/routes';
import { useAuth } from '@/features/auth/context';

const navigation = [
  ['Catalog overview', routes.adminCatalog, 'pi pi-chart-bar'],
  ['Products', routes.adminProducts, 'pi pi-box'],
  ['Categories', routes.adminCategories, 'pi pi-tags'],
  ['Media library', routes.adminCatalogMedia, 'pi pi-images'],
] as const;

export function AdminLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const trigger = useRef<HTMLButtonElement>(null);
  const { user } = useAuth();
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);
  const close = () => {
    setMobileOpen(false);
    window.setTimeout(() => trigger.current?.focus());
  };
  const sidebar = (
    <>
      <Link className="admin-brand" to={routes.adminCatalog}>
        <span className="wordmark__mark" aria-hidden="true">
          E
        </span>
        <span>Emazy Admin</span>
      </Link>
      <nav className="admin-navigation" aria-label="Catalog administration">
        {navigation.map(([label, to, icon]) => (
          <NavLink
            key={to}
            to={to}
            end={to === routes.adminCatalog}
            onClick={() => mobileOpen && close()}
          >
            <i className={icon} aria-hidden="true" /> {label}
          </NavLink>
        ))}
      </nav>
      <div className="admin-sidebar__footer">
        <Link to={routes.home}>
          <i className="pi pi-external-link" /> Storefront preview
        </Link>
        <Link to={routes.account}>
          <i className="pi pi-user" /> Account
        </Link>
        <Link to={routes.logout}>
          <i className="pi pi-sign-out" /> Sign out
        </Link>
      </div>
    </>
  );
  return (
    <div className="admin-shell">
      <a className="skip-link" href="#admin-main">
        Skip to admin content
      </a>
      <aside className="admin-sidebar">{sidebar}</aside>
      <header className="admin-topbar">
        <button
          ref={trigger}
          type="button"
          className="icon-button admin-menu-button"
          aria-label="Open admin navigation"
          aria-expanded={mobileOpen}
          onClick={() => setMobileOpen(true)}
        >
          <i className="pi pi-bars" aria-hidden="true" />
        </button>
        <div>
          <strong>Catalog administration</strong>
          <span>Admin · {user?.displayName}</span>
        </div>
        <Link to={routes.home}>View storefront</Link>
      </header>
      {mobileOpen && (
        <div
          className="admin-mobile-overlay"
          role="presentation"
          onMouseDown={(event) => event.target === event.currentTarget && close()}
        >
          <aside className="admin-mobile-drawer" aria-label="Mobile admin navigation">
            <button
              type="button"
              className="icon-button"
              aria-label="Close admin navigation"
              onClick={close}
            >
              <i className="pi pi-times" />
            </button>
            {sidebar}
          </aside>
        </div>
      )}
      <main id="admin-main" className="admin-main" tabIndex={-1}>
        <Outlet />
      </main>
    </div>
  );
}
